import React, { useEffect, useState, useCallback } from "react";
import { useDepartmentStore } from "../../store/useDepartmentStore";
import { WidgetContextRegistry } from "./WidgetContextRegistry";
import { isStepReady } from "./types";

interface OverlayState {
  top: number;
  left: number;
  width: number;
  height: number;
  label: string;
  contextType: string;
}

export function WidgetContextOverlay({ active }: { active: boolean }) {
  const [highlight, setHighlight] = useState<OverlayState | null>(null);
  const stepStatuses = useDepartmentStore((s) => s.stepStatuses);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest("[data-context-id]") as HTMLElement | null;
      if (!target) {
        setHighlight(null);
        return;
      }

      const contextId = target.getAttribute("data-context-id") || "";
      const contextType = target.getAttribute("data-context-type") || "";

      // Don't highlight widgets whose pipeline step hasn't produced data yet
      if (!isStepReady(contextType, stepStatuses)) {
        setHighlight(null);
        return;
      }

      // Don't highlight if extractor is no longer registered (unmounted)
      if (!WidgetContextRegistry.lookup(contextId)) {
        setHighlight(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      const label = target.getAttribute("data-context-label") || contextType;
      setHighlight({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        label,
        contextType,
      });
    },
    [stepStatuses],
  );

  useEffect(() => {
    if (!active) {
      setHighlight(null);
      return;
    }
    document.addEventListener("mousemove", handleMouseMove, true);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove, true);
    };
  }, [active, handleMouseMove]);

  if (!active) return null;

  return (
    <>
      {/* Global crosshair cursor + disable interactive children so clicks hit the extractable wrapper */}
      <style>{`
        body.widget-pick-mode,
        body.widget-pick-mode * {
          cursor: crosshair !important;
        }
        body.widget-pick-mode [data-context-id] * {
          pointer-events: none;
        }
        body.widget-pick-mode [data-context-id] {
          pointer-events: auto;
        }
      `}</style>

      {/* Highlight overlay */}
      {highlight && (
        <div
          className="fixed z-[9999] pointer-events-none border-2 border-cyan-400 rounded-lg transition-all duration-200"
          style={{
            top: highlight.top - 2,
            left: highlight.left - 2,
            width: highlight.width + 4,
            height: highlight.height + 4,
            boxShadow: "0 0 12px rgba(34, 211, 238, 0.35), inset 0 0 12px rgba(34, 211, 238, 0.08)",
          }}
        >
          {/* Floating label */}
          <div className="ds-micro-strong absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-cyan-500/90 px-2 py-0.5 text-white shadow-md backdrop-blur-sm">
            {highlight.label}
          </div>
        </div>
      )}
    </>
  );
}
